import React, { useState, useRef } from "react";
import { FaCamera, FaFileAlt, FaTimes, FaRedo, FaUpload, FaCheckCircle } from "react-icons/fa";
import DocumentScannerModal from "./DocumentScannerModal";
import { compressImageForUpload, formatFileSize, MAX_REPORT_IMAGE_BYTES } from "../../utils/imageScannerUtils";

export default function FileUploadWithScanner({
  label,
  name,
  accept = ".pdf,.jpg,.jpeg,.png",
  required = false,
  value = null,
  onChange,
  scannerTitle = "Scan Document",
  targetFileName = "scanned_document.jpg",
  helpText = null,
  className = "",
  compact = false,
}) {
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef(null);

  const prepareFile = async (file) => {
    if (!file || !onChange) return;
    setIsCompressing(true);
    try {
      onChange(await compressImageForUpload(file));
    } catch (error) {
      // Keep the selected file available if the browser cannot decode its format.
      console.warn("Image compression skipped:", error);
      onChange(file);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleStandardFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    await prepareFile(file);
    // Reset file input so re-selecting same file works
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleScannedComplete = async (file) => {
    await prepareFile(file);
  };

  const handleRemove = () => {
    if (onChange) {
      onChange(null);
    }
  };

  // Determine display values
  const hasValue = Boolean(value);
  const isFileInstance = value instanceof File;
  const fileName = isFileInstance ? value.name : typeof value === "string" ? value.split("/").pop() : "Attached Document";
  const fileSize = isFileInstance ? formatFileSize(value.size) : null;
  const isImage = isFileInstance ? value.type?.startsWith("image/") : typeof value === "string" ? /\.(jpg|jpeg|png|webp)/i.test(value) : false;
  const previewUrl = isFileInstance ? URL.createObjectURL(value) : typeof value === "string" ? value : null;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-slate-700 font-semibold text-xs mb-1">
          {label} {required && <span className="text-rose-600">*</span>}
        </label>
      )}

      {/* Hidden standard file input */}
      <input
        ref={fileInputRef}
        type="file"
        name={name}
        accept={accept}
        className="hidden"
        onChange={handleStandardFileChange}
      />

      {/* STATE 1: FILE ATTACHED (PREVIEW BADGE) */}
      {hasValue ? (
        <div className="flex items-center justify-between gap-3 p-2 bg-emerald-50/70 border border-emerald-300 rounded-xl transition">
          <div className="flex items-center gap-2.5 min-w-0">
            {isImage && previewUrl ? (
              <img
                src={previewUrl}
                alt="Doc preview"
                className="w-10 h-10 object-cover rounded-lg border border-emerald-200 shrink-0 bg-white"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                <FaFileAlt size={18} />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <FaCheckCircle className="text-emerald-600 text-xs shrink-0" />
                <span className="text-xs font-bold text-slate-900 truncate block">
                  {fileName}
                </span>
              </div>
              <div className="text-[10px] text-emerald-700 font-medium flex items-center gap-1 mt-0.5">
                {fileSize ? <span>{fileSize} &bull; Attached</span> : <span>Attached file</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setIsScannerOpen(true)}
              className="p-1.5 text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg transition"
              title="Re-scan document"
            >
              <FaRedo />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 text-xs text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
              title="Remove document"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      ) : (
        /* STATE 2: NO FILE (ACTIONS: BROWSE OR SCAN) */
        <div className="flex flex-wrap items-center gap-2">
          {/* Scan Document Button */}
          <button
            type="button"
            onClick={() => setIsScannerOpen(true)}
            className="flex-1 min-w-[120px] py-1.5 px-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition cursor-pointer"
          >
            <FaCamera className="text-emerald-200" />
            <span>Scan Document</span>
          </button>

          {/* Standard Browse Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer"
          >
            <FaUpload className="text-slate-500 text-[11px]" />
            <span>Choose File</span>
          </button>
        </div>
      )}

      {helpText && <p className="text-[10px] text-slate-400 mt-1">{helpText}</p>}
      {isCompressing && <p className="text-[10px] text-emerald-700 font-medium">Optimizing image for upload (max {formatFileSize(MAX_REPORT_IMAGE_BYTES)})…</p>}

      {/* Modal Scanner */}
      <DocumentScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onComplete={handleScannedComplete}
        title={scannerTitle}
        targetFileName={targetFileName}
      />
    </div>
  );
}
